// src/pages/reports/LoginOtp/ReportsLogFilter.tsx
import React, { useEffect, useRef, useState, forwardRef, Dispatch, SetStateAction } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Checkbox,
  CircularProgress,
  InputAdornment,
  GlobalStyles,
} from "@mui/material";
// ⛔️ Autocomplete removed (not needed with custom searchable dropdown)
// import Autocomplete from "@mui/material/Autocomplete";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getToken } from "../../../utils/utils";
import { getReportsFilterOptions_Lo } from "../api";
import { getalllocations, getalloutlettype } from "../../assign-task/api";

import arrowLeft from "../../../assests/images/arrow-left.svg";
import arrowRight from "../../../assests/images/arrow-right.png";
import doubleArrowLeft from "../../../assests/images/double-arrow-left.svg";
import doubleArrowRight from "../../../assests/images/double-arrow-right.svg";

const SELECT_ALL_LABEL = "Select All";

const CLEAR_X_DATA_URI =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path fill="#888" d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59L7.11 5.7A1 1 0 0 0 5.7 7.11L10.59 12l-4.9 4.89a1 1 0 1 0 1.41 1.42L12 13.41l4.89 4.9a1 1 0 0 0 1.42-1.42L13.41 12l4.9-4.89a1 1 0 0 0-.01-1.4Z"/></svg>`
  );

/** Rewards-style small arrow that rotates and is clickable */
const CustomArrow = ({ open, onClick }: { open?: boolean; onClick?: () => void }) => (
  <span
    onClick={(e) => {
      e.stopPropagation();
      onClick?.();
    }}
    style={{
      display: "inline-block",
      width: 10,
      height: 10,
      borderRight: "2px solid #222",
      borderBottom: "2px solid #222",
      transform: open ? "rotate(-135deg)" : "rotate(45deg)",
      transformOrigin: "center center",
      transition: "transform 0.18s ease",
      marginLeft: 12,
      position: "relative",
      cursor: "pointer",
      boxSizing: "border-box",
    }}
  />
);

/** Forwarded text input used by react-datepicker — clickable custom arrow */
const ForwardedTextInput = forwardRef<HTMLInputElement, any>(
  ({ value, onClick, placeholder, style, sx, ...rest }, ref) => {
    const mergedStyle = { paddingRight: 40, ...(style || {}), width: "100%" };
    return (
      <TextField
        size="small"
        variant="outlined"
        inputRef={ref}
        onClick={onClick}
        value={value}
        placeholder={placeholder || "Select date range"}
        fullWidth
        {...rest}
        sx={{ position: "relative", width: "100%", ...(sx || {}) }}
        InputProps={{
          ...(rest.InputProps || {}),
          endAdornment: (
            <>
              {rest.InputProps?.endAdornment}
              <InputAdornment position="end" sx={{ position: "absolute", right: 10 }}>
                <CustomArrow
                  open={false}
                  onClick={() => {
                    try {
                      onClick && (onClick as unknown as () => void)();
                    } catch {
                      try {
                        onClick?.();
                      } catch {}
                    }
                  }}
                />
              </InputAdornment>
            </>
          ),
        }}
        inputProps={{
          ...(rest.inputProps || {}),
          style: mergedStyle,
        }}
      />
    );
  }
);
ForwardedTextInput.displayName = "ForwardedTextInput";

/** 🔎 Searchable multi-select (parity with ReportsFilterPopup) */
function SearchableMultiSelect({
  label,
  options,
  value,
  onChange,
  loading,
  formatOptionLabel,
}: {
  label: string;
  options: string[];
  value: string[];
  onChange: (vals: string[]) => void;
  loading?: boolean;
  /** display-only formatter; raw values preserved for backend */
  formatOptionLabel?: (s: string) => string;
}) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const fmt = (s: string) => (formatOptionLabel ? formatOptionLabel(s) : s);

  // close on outside click / ESC
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearchValue("");
      }
    };
    const onEsc = (e: KeyboardEvent) => e.key === "Escape" && (setOpen(false), setSearchValue(""));
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const filtered = (options || []).filter((opt) =>
    fmt(opt).toLowerCase().includes(searchValue.toLowerCase())
  );

  const allSelected = value.length > 0 && value.length === options.length;
  const displayedValue =
    value.length === 0 ? label : value.length <= 2 ? value.map(fmt).join(", ") : `${value.slice(0, 2).map(fmt).join(", ")}…`;

  const toggleOption = (opt: string) =>
    value.includes(opt) ? onChange(value.filter((v) => v !== opt)) : onChange([...value, opt]);

  const toggleSelectAll = () => (allSelected ? onChange([]) : onChange(options.slice()));

  return (
    <Box ref={containerRef} sx={{ width: "100%", position: "relative", userSelect: "none" }}>
      <TextField
        size="small"
        variant="outlined"
        value={displayedValue}
        onClick={() => setOpen((v) => !v)}
        placeholder={label}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <>
              {loading ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : null}
              <InputAdornment position="end" sx={{ cursor: "pointer" }}>
                <CustomArrow open={open} onClick={() => setOpen((v) => !v)} />
              </InputAdornment>
            </>
          ),
          sx: {
            cursor: "pointer",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
        }}
        fullWidth
      />

      {open && (
        <Box
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            width: "100%",
            maxHeight: 250,
            overflowY: "auto",
            border: "1px solid #ccc",
            backgroundColor: "#fff",
            zIndex: 3500,
            mt: 0.5,
            borderRadius: 1,
            boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Box sx={{ px: 1, py: 0.5 }}>
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search..."
              style={{
                width: "100%",
                padding: "10px 10px",
                borderRadius: 4,
                border: "1px solid #ddd",
                fontSize: "0.9rem",
                marginBottom: 8,
                display: "block",
                marginLeft: "auto",
                marginRight: "auto",
                marginTop: "5px",
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            />
          </Box>

          <Box sx={{ px: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1, cursor: "pointer" }} onClick={toggleSelectAll}>
              <Checkbox checked={allSelected} indeterminate={value.length > 0 && !allSelected} />
              <Typography variant="body2">{SELECT_ALL_LABEL}</Typography>
            </Box>

            {filtered.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 1 }}>
                No options found
              </Typography>
            )}

            {filtered.map((opt) => {
              const checked = value.includes(opt);
              return (
                <Box
                  key={opt}
                  sx={{ display: "flex", alignItems: "center", py: 0.5, cursor: "pointer" }}
                  onClick={() => toggleOption(opt)}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <Checkbox checked={checked} />
                  <Typography variant="body2">{fmt(opt)}</Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export interface ReportsLogFilterProps {
  isOpen?: boolean;
  shouldFetch?: boolean;
  onFetched?: () => void;
  initialFilters?: Partial<{
    locations: string[] | null;
    outlet_category: string[] | null;
    // device_type: string[] | null;
    pmi_entity_code: string[] | null;
    outlet_name: string[] | null;
    lastLogin: { from: string | null; to: string | null } | null;
  }> | null;

  onApplyFilters: (filters: {
    locations: string[];
    outlet_category: string[];
    // device_type: string[];
    pmi_entity_code: string[];
    outlet_name: string[];
    lastLogin: { from: string | null; to: string | null };
  }) => void;
  onCancel?: () => void;

  dateRange: [Date | null, Date | null];
  setDateRange: Dispatch<SetStateAction<[Date | null, Date | null]>>;
}

export default function ReportsLogFilter({
  isOpen = false,
  shouldFetch = false,
  onFetched,
  initialFilters = null,

  onApplyFilters,
  onCancel,
  dateRange,
  setDateRange,
}: ReportsLogFilterProps) {
  const token = getToken();

  /** Lock body scroll while popup is open (Rewards parity) */
  useEffect(() => {
    if (!isOpen) return;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const prev = {
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      overflow: document.body.style.overflow,
    };
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.position = prev.position || "";
      document.body.style.top = prev.top || "";
      document.body.style.left = prev.left || "";
      document.body.style.right = prev.right || "";
      document.body.style.overflow = prev.overflow || "";
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  // options
  const [locationOptions, setLocationOptions] = useState<string[]>([]);
  const [outletCategoryOptions, setOutletCategoryOptions] = useState<string[]>([]);
  const [pmiOptions, setPmiOptions] = useState<string[]>([]);
  const [outletNameOptions, setOutletNameOptions] = useState<string[]>([]);

  // selections
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedOutletCategories, setSelectedOutletCategories] = useState<string[]>([]);
  const [selectedPmi, setSelectedPmi] = useState<string[]>([]);
  const [selectedOutletNames, setSelectedOutletNames] = useState<string[]>([]);

  // loading flags
  const [loadingLocCat, setLoadingLocCat] = useState(false);
  const [loadingPmiOutlet, setLoadingPmiOutlet] = useState(false);

  // abort control & bookkeeping
  const pmiOutletAbortRef = useRef<AbortController | null>(null);
  const onFetchedCalledRef = useRef(false);

  // Cache PMI/Outlet by locations (TTL)
  const cacheRef = useRef<Record<
    string,
    { pmi: string[]; outlets: string[]; expiry: number }
  >>({});
  const TTL_MS = 60 * 1000;

  // Stable keys to drive cache + fetches
  const locationsKey = React.useMemo(
    () => (Array.isArray(selectedLocations) ? [...selectedLocations].sort().join("|") : ""),
    [selectedLocations]
  );
  const outletNamesKey = React.useMemo(
    () => (Array.isArray(selectedOutletNames) ? [...selectedOutletNames].sort().join("|") : ""),
    [selectedOutletNames]
  );
  const pmiKey = React.useMemo(
    () => (Array.isArray(selectedPmi) ? [...selectedPmi].sort().join("|") : ""),
    [selectedPmi]
  );

  const arraysEqual = (a: string[], b: string[]) =>
    a.length === b.length && a.every((v, i) => v === b[i]);

  /** Initial fetch (Locations & Outlet Category) — Rewards-style timing */
  useEffect(() => {
    if (!isOpen || !shouldFetch) return;
    let cancelled = false;

    (async () => {
      try {
        setLoadingLocCat(true);
        const [locRes, catRes] = await Promise.allSettled([getalllocations(), getalloutlettype()]);

        if (cancelled) return;

        // locations
        if (locRes.status === "fulfilled") {
          const val: any = locRes.value;
          const arr =
            (Array.isArray(val?.data?.data) && val.data.data) ||
            (Array.isArray(val?.data) && val.data) ||
            (Array.isArray(val?.Data) && val.Data) ||
            (Array.isArray(val) && val) ||
            [];
          setLocationOptions(arr.map((s: any) => String(s ?? "").trim()).filter(Boolean));
        } else {
          setLocationOptions([]);
        }

        // outlet categories
        if (catRes.status === "fulfilled") {
          const val: any = catRes.value;
          const arr =
            (Array.isArray(val?.data?.data) && val.data.data) ||
            (Array.isArray(val?.data) && val.data) ||
            (Array.isArray(val?.Data) && val.Data) ||
            (Array.isArray(val) && val) ||
            [];
          setOutletCategoryOptions(arr.map((s: any) => String(s ?? "").trim()).filter(Boolean));
        } else {
          setOutletCategoryOptions([]);
        }

        if (!cancelled && !onFetchedCalledRef.current) {
          onFetchedCalledRef.current = true;
          onFetched?.();
        }
      } catch {
        if (!cancelled) {
          setLocationOptions([]);
          setOutletCategoryOptions([]);
        }
      } finally {
        if (!cancelled) setLoadingLocCat(false);
      }
    })();

    return () => {
      cancelled = true;
      onFetchedCalledRef.current = false;
    };
  }, [isOpen, shouldFetch, onFetched]);

  /** Interdependent fetch for PMI & Outlet Names (with cache + pruning) */
  useEffect(() => {
    if (!isOpen) return;

    // abort previous call if any
    if (pmiOutletAbortRef.current) {
      try {
        pmiOutletAbortRef.current.abort();
      } catch {}
    }
    const ctrl = new AbortController();
    pmiOutletAbortRef.current = ctrl;

    (async () => {
      try {
        setLoadingPmiOutlet(true);

        const now = Date.now();
        const hasOutletFilter = selectedOutletNames.length > 0;
        const hasPmiFilter = selectedPmi.length > 0;

        // Use cache only when scoped purely by locations (no outlet / no PMI)
        if (!hasOutletFilter && !hasPmiFilter) {
          const cached = cacheRef.current[locationsKey];
          if (cached && cached.expiry > now) {
            setPmiOptions(cached.pmi);
            setOutletNameOptions(cached.outlets);

            // prune selections based on cached options (guarded)
            setSelectedPmi((prev) => {
              const pruned = prev.filter((p) => cached.pmi.includes(p));
              return arraysEqual(pruned, prev) ? prev : pruned;
            });
            setSelectedOutletNames((prev) => {
              const pruned = prev.filter((n) => cached.outlets.includes(n));
              return arraysEqual(pruned, prev) ? prev : pruned;
            });
            return;
          }
        }

        // Fetch narrowed by locations + optional outlet_name / pmi_entity_code
        const fo = await getReportsFilterOptions_Lo(
          {
            locations: selectedLocations.length ? selectedLocations : null,
            outlet_name: hasOutletFilter ? selectedOutletNames : null,
            pmi_entity_code: hasPmiFilter ? selectedPmi : null,
          },
          token,
          ctrl.signal
        );

        if (ctrl.signal.aborted) return;

        const pmiList = Array.isArray(fo?.pmiEntityCodes) ? fo.pmiEntityCodes : [];
        const outletList = Array.isArray(fo?.outletNames) ? fo.outletNames : [];

        setPmiOptions(pmiList);
        setOutletNameOptions(outletList);

        if (!hasOutletFilter && !hasPmiFilter) {
          cacheRef.current[locationsKey] = {
            pmi: pmiList,
            outlets: outletList,
            expiry: now + TTL_MS,
          };
        }

        // prune selections if options shrink (guarded)
        setSelectedPmi((prev) => {
          const pruned = prev.filter((p) => pmiList.includes(p));
          return arraysEqual(pruned, prev) ? prev : pruned;
        });
        setSelectedOutletNames((prev) => {
          const pruned = prev.filter((n) => outletList.includes(n));
          return arraysEqual(pruned, prev) ? prev : pruned;
        });
      } catch {
        if (!ctrl.signal.aborted) {
          setPmiOptions([]);
          setOutletNameOptions([]);
        }
      } finally {
        if (pmiOutletAbortRef.current === ctrl) pmiOutletAbortRef.current = null;
        setLoadingPmiOutlet(false);
      }
    })();

    return () => {
      if (pmiOutletAbortRef.current) {
        try {
          pmiOutletAbortRef.current.abort();
        } catch {}
        pmiOutletAbortRef.current = null;
      }
    };
    // Depend on stable keys to avoid ref loops
  }, [isOpen, locationsKey, outletNamesKey, pmiKey, token]);

  /** Rehydrate from initialFilters (parity with ReportsFilterPopup) */
  useEffect(() => {
    if (!initialFilters) return;

    setSelectedLocations(initialFilters.locations ?? []);
    setSelectedOutletCategories(initialFilters.outlet_category ?? []);
    setSelectedPmi(initialFilters.pmi_entity_code ?? []);
    setSelectedOutletNames(initialFilters.outlet_name ?? []);

    const fromStr = initialFilters.lastLogin?.from || null;
    const toStr = initialFilters.lastLogin?.to || null;
    const from = fromStr ? new Date(fromStr) : null;
    const to = toStr ? new Date(toStr) : null;

    try {
      const parentStart = dateRange?.[0] ?? null;
      const parentEnd = dateRange?.[1] ?? null;
      const startChanged = (parentStart && from) ? parentStart.getTime() !== from.getTime() : parentStart !== from;
      const endChanged = (parentEnd && to) ? parentEnd.getTime() !== to.getTime() : parentEnd !== to;
      if (startChanged || endChanged) setDateRange([from, to]);
    } catch {
      setDateRange([from, to]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilters]);

  /** Helpers */
  const formatRangeText = (range: [Date | null, Date | null]) => {
    if (!range || (!range[0] && !range[1])) return "Select date range";
    const s = range[0] ? range[0].toLocaleDateString() : "";
    const e = range[1] ? range[1].toLocaleDateString() : "";
    return s && e ? `${s} - ${e}` : s || e || "Select date range";
  };

  /** Header renderer (double arrows) for DatePicker */
  const btnResetStyle: React.CSSProperties = {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    padding: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const renderHeaderWithIcons = ({
    date,
    decreaseMonth,
    increaseMonth,
    decreaseYear,
    increaseYear,
  }: {
    date: Date;
    decreaseMonth: () => void;
    increaseMonth: () => void;
    decreaseYear: () => void;
    increaseYear: () => void;
  }) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
      <button type="button" onClick={decreaseYear} aria-label="Previous year" style={btnResetStyle}>
        <img src={doubleArrowLeft} alt="Previous year" />
      </button>
      <button type="button" onClick={decreaseMonth} aria-label="Previous month" style={btnResetStyle}>
        <img src={arrowLeft} alt="Previous month" />
      </button>

      <span style={{ margin: "0 10px", fontWeight: 600 }}>
        {date.toLocaleString("default", { year: "numeric", month: "long" })}
      </span>

      <button type="button" onClick={increaseMonth} aria-label="Next month" style={btnResetStyle}>
        <img src={arrowRight} alt="Next month" />
      </button>
      <button type="button" onClick={increaseYear} aria-label="Next year" style={btnResetStyle}>
        <img src={doubleArrowRight} alt="Next year" />
      </button>
    </div>
  );

  /** Actions */
  const handleClear = () => {
    setSelectedLocations([]);
    setSelectedOutletCategories([]);
    setSelectedPmi([]);
    setSelectedOutletNames([]);
    setDateRange([null, null]);

    onApplyFilters({
      locations: [],
      outlet_category: [],
      pmi_entity_code: [],
      outlet_name: [],
      lastLogin: { from: null, to: null },
    });
    onCancel?.();
  };

  const handleApply = () => {
    onApplyFilters({
      locations: selectedLocations,
      outlet_category: selectedOutletCategories,
      pmi_entity_code: selectedPmi,
      outlet_name: selectedOutletNames,
      lastLogin: {
        from: dateRange[0] ? dateRange[0].toISOString() : null,
        to: dateRange[1] ? dateRange[1].toISOString() : null,
      },
    });
    onCancel?.();
  };

  /** UI */
  return (
    <Box
      sx={{
        width: 420,
        maxHeight: "80vh",
        background: "#fff",
        borderRadius: 2,
        p: 0,
        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <GlobalStyles
        styles={{
          '.MuiAutocomplete-option[aria-selected="true"]': {
            backgroundColor: 'transparent !important',
          },
          '.MuiAutocomplete-option.Mui-focused': {
            backgroundColor: 'transparent !important',
          },
          '.MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
            backgroundColor: 'transparent !important',
          },
          '.react-datepicker-wrapper': {
            width: '100% !important',
          },
        }}
      />

      {/* Header */}
      <Box sx={{ p: 2.5, borderBottom: "1px solid #eee" }}>
        <Typography sx={{ fontWeight: 600 }}>Apply Filters</Typography>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: "auto", p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Select Locations</Typography>
          <SearchableMultiSelect
            label="Select Locations"
            options={locationOptions}
            value={selectedLocations}
            onChange={setSelectedLocations}
            loading={loadingLocCat}
          />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Last Login</Typography>
          <Box sx={{ width: "100%" }}>
            <DatePicker
              selectsRange
              startDate={dateRange[0] ?? undefined}
              endDate={dateRange[1] ?? undefined}
              onChange={(range: [Date | null, Date | null]) => setDateRange(range)}
              dateFormat="yyyy-MM-dd"
              renderCustomHeader={renderHeaderWithIcons}
              customInput={
                <ForwardedTextInput
                  placeholder={formatRangeText(dateRange)}
                  key={`lastlogin-${dateRange[0]?.getTime() ?? 0}-${dateRange[1]?.getTime() ?? 0}`}
                  InputProps={{
                    endAdornment: (dateRange[0] || dateRange[1]) ? (
                      <img
                        onClick={(e) => {
                          e.stopPropagation();
                          setDateRange([null, null]);
                        }}
                        src={CLEAR_X_DATA_URI}
                        alt="Clear"
                        style={{ height: 18, width: 18, cursor: "pointer", marginRight: 10, paddingTop: "4px" }}
                      />
                    ) : undefined,
                  }}
                />
              }
            />
          </Box>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Select Outlet Category</Typography>
        <SearchableMultiSelect
            label="Select Outlet Category"
            options={outletCategoryOptions}
            value={selectedOutletCategories}
            onChange={setSelectedOutletCategories}
            loading={loadingLocCat}
          />
        </Box>

        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Select PMI Entity Code</Typography>
          <SearchableMultiSelect
            label="Select PMI Entity Code"
            options={pmiOptions}
            value={selectedPmi}
            onChange={setSelectedPmi}
            loading={loadingPmiOutlet}
          />
        </Box>

        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Select Outlet Names</Typography>
          <SearchableMultiSelect
            label="Select Outlet Names"
            options={outletNameOptions}
            value={selectedOutletNames}
            onChange={setSelectedOutletNames}
            loading={loadingPmiOutlet}
          />
        </Box>

        {/* Last Login DatePicker */}
      
      </Box>

      {/* Footer */}
      <Box sx={{ p: 2.5, borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end", gap: 1.25 }}>
        <Button onClick={handleClear} sx={{ border: "1px solid #E7E8E9", color: "#000", borderRadius: 2, height: 40 }}>
          CLEAR ALL
        </Button>
        <Button onClick={handleApply} sx={{ border: "1px solid #C8172F", color: "#C8172F", borderRadius: 2, height: 40 }}>
          APPLY
        </Button>
      </Box>
    </Box>
  );
}
